import { useEffect, useState } from "react";
import { ChevronLeft, MapPin, Package, Store, Wrench, ShieldCheck, Search, Send } from "lucide-react";
import client, { extractError } from "@/api";
import ListingCoverImage from "@/components/ListingCoverImage";

const TYPE_ICON = { PRODUCT: Package, CAPACITY: Store, SERVICE: Wrench };
const TYPE_LABEL = { PRODUCT: "Product", CAPACITY: "Capacity", SERVICE: "Service" };

function TradeRequestForm({ listing, onSubmitted, onCancel }) {
  const [account, setAccount] = useState(null);
  const [quantity, setQuantity] = useState(listing.quantity ? 1 : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => { client.get("/credits/account").then((r) => setAccount(r.data)).catch(() => setAccount(null)); }, []);

  const q = Number(quantity || 0);
  const unit = listing.indicative_value || 0;
  const tradeValue = q >= 1 ? unit * q : unit;
  const fee = Math.round(tradeValue * 0.10);
  // Barter Credits back only the trade value; facilitator fee is billed separately.
  const total = tradeValue;
  const available = account?.available_credit || 0;
  const canSubmit = tradeValue > 0 && total <= available && (!listing.quantity || (q >= 1 && q <= listing.quantity));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { listing_id: listing.id, buyer_note: note || null };
      if (listing.quantity) payload.requested_quantity = q;
      const r = await client.post("/trade-requests", payload);
      setSuccess(true); onSubmitted?.(r.data);
    } catch (err) { setError(extractError(err, "Unable to submit trade request.")); }
    finally { setBusy(false); }
  };

  if (success) {
    return (
      <div className="section-block" data-testid="trade-request-success">
        <p className="eyebrow">SUBMITTED</p>
        <h3>Trade request sent for review</h3>
        <p className="muted small">Barter India will review this request. You can track its status under <strong>Trade Requests</strong>.</p>
        <button type="button" className="ghost-button" onClick={onCancel}>Close</button>
      </div>
    );
  }

  return (
    <form className="company-form" onSubmit={submit} data-testid="trade-request-form">
      <div className="section-heading"><div><p className="eyebrow">REQUEST TRADE</p><h3>Propose a trade with {listing.company?.brand_name || listing.company?.legal_name}</h3></div></div>
      {error && <div className="form-error" data-testid="trade-request-error">{error}</div>}
      <div className="form-grid">
        {listing.quantity ? (
          <label>Requested quantity ({listing.quantity_unit || "units"}) *
            <input type="number" required min={1} max={listing.quantity} value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="trade-request-quantity" />
          </label>
        ) : (
          <label>Quantity<input value="Single engagement" disabled /></label>
        )}
        <label>Note to Barter India (optional)<textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} data-testid="trade-request-note" /></label>
      </div>
      <section className="detail-grid" data-testid="trade-request-summary">
        <article><p className="eyebrow">UNIT VALUE</p><p>₹{unit.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">TRADE VALUE</p><p><strong data-testid="summary-trade-value">₹{tradeValue.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">FACILITATOR FEE (10%)</p><p data-testid="summary-fee">₹{fee.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">TOTAL CREDIT REQUIRED</p><p><strong data-testid="summary-total">₹{total.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">AVAILABLE CREDIT</p><p data-testid="summary-available">₹{available.toLocaleString("en-IN")}</p></article>
      </section>
      {total > available && <div className="form-error" data-testid="insufficient-credit">Total credit requirement exceeds your available Barter Credit.</div>}
      <div className="form-actions">
        <button type="button" className="ghost-button" onClick={onCancel} data-testid="trade-request-cancel">Cancel</button>
        <button type="submit" className="primary-button" disabled={busy || !canSubmit} data-testid="trade-request-submit"><Send size={13} /> {busy ? "Submitting…" : "Submit for admin review"}</button>
      </div>
    </form>
  );
}

export default function MarketplacePage({ user }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (!user || user.role === "COMPANY_ADMIN" || user.role === "COMPANY_USER") {
      client.get("/companies/me").then((r) => setCompany(r.data || null)).catch(() => setCompany(null));
    }
  }, [user]);

  useEffect(() => {
    Promise.all([client.get("/reference/categories"), client.get("/reference/states")])
      .then(([c, s]) => { setCategories(c.data); setStates(s.data); }).catch(() => {});
  }, []);

  const load = async (targetPage = 1) => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set("listing_type", typeFilter);
      if (categoryFilter) params.set("category_id", categoryFilter);
      if (stateFilter) params.set("state_id", stateFilter);
      if (search) params.set("search", search);
      params.set("page", targetPage); params.set("page_size", 24);
      const res = await client.get(`/marketplace/listings?${params.toString()}`);
      setItems(res.data.items || []); setTotal(res.data.total || 0);
      setPage(res.data.page || 1); setTotalPages(res.data.total_pages || 1);
    } catch (err) { setError(extractError(err, "Unable to load marketplace.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [typeFilter, categoryFilter, stateFilter]);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    client.get(`/marketplace/listings/${detailId}`).then((r) => setDetail(r.data)).catch((err) => setError(extractError(err)));
  }, [detailId]);

  const viewMedia = async (mediaId) => {
    if (!detail) return;
    try {
      const res = await client.get(`/listings/${detail.id}/media/${mediaId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(extractError(err)); }
  };

  if (detailId && detail) {
    const Icon = TYPE_ICON[detail.listing_type] || Package;
    return (
      <div className="marketplace-detail" data-testid="marketplace-detail">
        <button type="button" className="link-button back" data-testid="marketplace-back" onClick={() => setDetailId(null)}><ChevronLeft size={14} /> Back to marketplace</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow"><Icon size={12} /> {TYPE_LABEL[detail.listing_type] || detail.listing_type}</p>
            <h2>{detail.title}</h2>
            <p className="muted">
              <ShieldCheck size={13} /> {detail.company.brand_name || detail.company.legal_name} · <MapPin size={13} /> {detail.city_name}, {detail.state_name}
            </p>
          </div>
          <div className="chip-stack">
            <span className="chip chip-info">₹{detail.indicative_value.toLocaleString("en-IN")}</span>
          </div>
        </header>
        <section className="detail-grid">
          <article><p className="eyebrow">CATEGORY</p><p>{detail.category_name}{detail.subcategory_name ? ` · ${detail.subcategory_name}` : ""}</p></article>
          <article><p className="eyebrow">QUANTITY</p><p>{detail.quantity ?? "—"} {detail.quantity_unit || ""}</p></article>
          <article><p className="eyebrow">MIN TRADE VALUE</p><p>{detail.minimum_trade_value ? `₹${detail.minimum_trade_value.toLocaleString("en-IN")}` : "—"}</p></article>
          <article><p className="eyebrow">PUBLISHED</p><p>{detail.published_at ? new Date(detail.published_at).toLocaleDateString() : "—"}</p></article>
          <article className="wide"><p className="eyebrow">SHORT</p><p>{detail.short_description}</p></article>
          <article className="wide"><p className="eyebrow">DETAILS</p><p>{detail.detailed_description}</p></article>
        </section>
        {(detail.media || []).length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">MEDIA</p><h3>Attachments</h3></div></div>
            <ul className="document-list">
              {detail.media.map((m) => (
                <li key={m.id}><strong>{m.media_type}</strong><small>{m.file_name}</small><button type="button" className="link-button" onClick={() => viewMedia(m.id)}>View</button></li>
              ))}
            </ul>
          </section>
        )}
        <div className="placeholder-card" data-testid="trade-request-cta">
          {(() => {
            const isOwnListing = company && detail.company && company.id === detail.company.id;
            const canRequest = user?.role === "COMPANY_ADMIN" && company?.status === "VERIFIED" && !isOwnListing;
            if (showRequestForm && canRequest) {
              return <TradeRequestForm listing={detail} onCancel={() => setShowRequestForm(false)} onSubmitted={() => {}} />;
            }
            if (isOwnListing) {
              return <><p className="eyebrow">YOUR LISTING</p><h3>This is one of your own listings.</h3><p className="muted">Buyers can request this offer. Manage it from <strong>My Listings</strong>.</p></>;
            }
            if (user?.role !== "COMPANY_ADMIN") {
              return <><p className="eyebrow">TRADE REQUESTS</p><h3>Company administrators can submit trade requests.</h3></>;
            }
            if (company?.status !== "VERIFIED") {
              return <><p className="eyebrow">VERIFICATION REQUIRED</p><h3>Only verified companies can submit trade requests.</h3><p className="muted">Complete verification from the Company tab.</p></>;
            }
            return (
              <>
                <p className="eyebrow">READY TO TRADE</p>
                <h3>Submit a trade request to {detail.company.brand_name || detail.company.legal_name}.</h3>
                <p className="muted">Barter India will review your request before credits are moved.</p>
                <button type="button" className="primary-button" data-testid="open-trade-request" onClick={() => setShowRequestForm(true)}><Send size={13} /> Request trade</button>
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-page" data-testid="marketplace-page">
      <div className="marketplace-hero">
        <div>
          <p className="eyebrow">BARTER INDIA MARKETPLACE</p>
          <h2>Discover live offers from verified businesses</h2>
        </div>
      </div>
      <div className="advanced-filters" data-testid="marketplace-filters">
        <div className="filter-tabs">
          {[{ value: "", label: "All" }, { value: "PRODUCT", label: "Products" }, { value: "CAPACITY", label: "Capacity" }, { value: "SERVICE", label: "Services" }].map((t) => (
            <button key={t.value || "all"} type="button" className={typeFilter === t.value ? "active" : ""} onClick={() => setTypeFilter(t.value)} data-testid={`marketplace-type-${t.value || "all"}`}>{t.label}</button>
          ))}
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="marketplace-category-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} data-testid="marketplace-state-filter">
          <option value="">All states</option>
          {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(1); }} className="search-row">
          <input placeholder="Search listings" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="marketplace-search-input" />
          <button type="submit" className="ghost-button"><Search size={12} /></button>
        </form>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading marketplace…</div> : (
        <>
          {items.length === 0 ? (
            <div className="empty-state" data-testid="marketplace-empty"><p><strong>No listings yet</strong></p><p className="muted small">Approved listings from verified companies will appear here.</p></div>
          ) : (
            <div className="marketplace-grid" data-testid="marketplace-grid">
              {items.map((l) => {
                const Icon = TYPE_ICON[l.listing_type] || Package;
                return (
                  <article
                    key={l.id}
                    className="listing-card"
                    data-testid={`marketplace-card-${l.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailId(l.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailId(l.id); }
                    }}
                  >
                    <div className="listing-card-media">
                      <ListingCoverImage
                        listingId={l.id}
                        coverImageId={l.cover_image_id}
                        alt={l.title}
                        placeholder={<Icon size={32} className="listing-cover-placeholder-icon" aria-hidden="true" />}
                      />
                      <span className="chip chip-info listing-cover-chip">{TYPE_LABEL[l.listing_type]}</span>
                    </div>
                    <div className="listing-card-body">
                      <h4>{l.title}</h4>
                      <p className="muted">{l.short_description}</p>
                      <div className="listing-card-meta">
                        <span><MapPin size={11} /> {l.city_name || "—"}</span>
                        <span>{l.category_name || "—"}</span>
                      </div>
                    </div>
                    <div className="listing-card-footer">
                      <div><small className="muted">by</small><strong>{l.company.brand_name || l.company.legal_name}</strong>{l.company.verified && <ShieldCheck size={12} className="verified-icon" />}</div>
                      <div className="listing-card-value">₹{l.indicative_value.toLocaleString("en-IN")}</div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          <div className="pagination">
            <button type="button" className="ghost-button" onClick={() => load(page - 1)} disabled={page <= 1} data-testid="marketplace-prev">Previous</button>
            <span>Page {page} of {totalPages} · {total} listings</span>
            <button type="button" className="ghost-button" onClick={() => load(page + 1)} disabled={page >= totalPages} data-testid="marketplace-next">Next</button>
          </div>
        </>
      )}
    </div>
  );
}
